import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, ArrowLeft, Send, ChevronRight, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

export default function FloatingAdminChat() {
    const { user } = useAuth();
    const location = useLocation();
    const isAdmin = user?.role === 'admin';

    const [open, setOpen] = useState(false);
    const [view, setView] = useState('list');
    const [previews, setPreviews] = useState([]);
    const [selected, setSelected] = useState(null);
    const [messages, setMessages] = useState([]);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);
    const [totalUnread, setTotalUnread] = useState(0);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    const hdrs = useCallback(() => ({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user?.token}`,
    }), [user?.token]);

    const loadPreviews = useCallback(() => {
        if (!isAdmin || !user?.token) return;
        fetch(`${API}/api/admin/previews`, { headers: hdrs() })
            .then(r => r.json())
            .then(data => {
                if (!Array.isArray(data)) return;
                setPreviews(data);
                setTotalUnread(data.reduce((s, p) => s + (p.open_feedback_count || 0), 0));
            })
            .catch(() => {});
    }, [isAdmin, user?.token, hdrs]);

    const loadMessages = useCallback(() => {
        if (!selected) return;
        fetch(`${API}/api/admin/previews/${selected.id}/feedback`, { headers: hdrs() })
            .then(r => r.json())
            .then(data => {
                if (Array.isArray(data)) {
                    setMessages(data);
                    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
                }
            })
            .catch(() => {});
    }, [selected, hdrs]);

    useEffect(() => { loadPreviews(); }, [loadPreviews]);

    useEffect(() => {
        const id = setInterval(loadPreviews, 20000);
        return () => clearInterval(id);
    }, [loadPreviews]);

    useEffect(() => {
        if (!selected) return;
        loadMessages();
        const id = setInterval(loadMessages, 15000);
        return () => clearInterval(id);
    }, [selected?.id]);

    const openChat = (preview) => {
        setSelected(preview);
        setMessages([]);
        setView('chat');
    };

    const goBack = () => {
        setView('list');
        setSelected(null);
        setMessages([]);
        loadPreviews();
    };

    const deleteMessage = async (fid) => {
        if (!selected || !window.confirm('Nachricht löschen?')) return;
        await fetch(`${API}/api/admin/previews/${selected.id}/feedback/${fid}`, {
            method: 'DELETE',
            headers: hdrs(),
        }).catch(() => {});
        loadMessages();
        loadPreviews();
    };

    const sendReply = async () => {
        const text = replyText.trim();
        if (!text || !selected || sending) return;
        setSending(true);
        await fetch(`${API}/api/admin/previews/${selected.id}/chat`, {
            method: 'POST',
            headers: hdrs(),
            body: JSON.stringify({ text }),
        }).catch(() => {});
        setReplyText('');
        setSending(false);
        loadMessages();
        loadPreviews();
        inputRef.current?.focus();
    };

    if (!isAdmin || !location.pathname.startsWith('/privat')) return null;

    const fmtTime = (ts) => ts
        ? new Date(ts).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
        : '';

    return (
        <div style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 1000 }}>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -8 }}
                        transition={{ duration: 0.15 }}
                        style={{
                            background: 'var(--card-bg)', border: '1px solid var(--border)',
                            borderRadius: '12px', width: '320px',
                            boxShadow: '0 8px 28px rgba(0,0,0,0.35)', marginBottom: '10px',
                            display: 'flex', flexDirection: 'column', maxHeight: '500px',
                        }}>

                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '13px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                            {view === 'chat' && (
                                <button onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', padding: 0 }}>
                                    <ArrowLeft size={16} />
                                </button>
                            )}
                            <MessageSquare size={15} color="var(--accent)" />
                            <span style={{ flex: 1, fontSize: '13px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {view === 'list' ? 'Kunden-Chat' : (selected?.title || 'Chat')}
                            </span>
                            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', padding: 0 }}>
                                <X size={16} />
                            </button>
                        </div>

                        {/* Preview list */}
                        {view === 'list' && (
                            <div style={{ flex: 1, overflowY: 'auto' }}>
                                {previews.length === 0 ? (
                                    <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                                        Keine Vorschauen vorhanden.
                                    </div>
                                ) : previews.map(p => (
                                    <button key={p.id} onClick={() => openChat(p)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '10px',
                                            width: '100%', padding: '11px 16px', textAlign: 'left',
                                            background: 'none', border: 'none', borderBottom: '1px solid var(--border)',
                                            cursor: 'pointer', color: 'var(--text-primary)', fontFamily: 'inherit',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {p.title || 'Ohne Titel'}
                                            </div>
                                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'monospace', marginTop: '2px' }}>
                                                {p.code}
                                            </div>
                                        </div>
                                        {p.open_feedback_count > 0 && (
                                            <span style={{
                                                background: '#ef4444', color: '#fff', fontSize: '11px', fontWeight: 700,
                                                borderRadius: '20px', minWidth: '18px', height: '18px',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                padding: '0 4px', flexShrink: 0,
                                            }}>
                                                {p.open_feedback_count}
                                            </span>
                                        )}
                                        <ChevronRight size={14} color="var(--text-secondary)" style={{ flexShrink: 0 }} />
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Chat view */}
                        {view === 'chat' && (
                            <>
                                <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {messages.length === 0 ? (
                                        <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px', padding: '20px 0' }}>
                                            Noch keine Nachrichten.
                                        </div>
                                    ) : messages.map(m => {
                                        const isMe = m.sender === 'admin';
                                        const delBtn = (
                                            <button onClick={() => deleteMessage(m.id)} title="Nachricht löschen"
                                                style={{
                                                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                                                    color: 'var(--text-secondary)', opacity: 0.45, display: 'flex',
                                                    alignSelf: 'flex-end', flexShrink: 0, transition: 'opacity 0.15s',
                                                }}
                                                onMouseEnter={e => { e.currentTarget.style.opacity = 1; }}
                                                onMouseLeave={e => { e.currentTarget.style.opacity = 0.45; }}>
                                                <Trash2 size={13} />
                                            </button>
                                        );
                                        return (
                                            <div key={m.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: '2px' }}>
                                                {isMe && delBtn}
                                                <div style={{
                                                    maxWidth: '85%', padding: '7px 11px',
                                                    borderRadius: isMe ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                                                    background: isMe ? 'var(--accent)' : 'var(--bg-secondary)',
                                                    color: isMe ? '#000' : 'var(--text-primary)',
                                                    fontSize: '13px', lineHeight: 1.5,
                                                    border: !isMe ? '1px solid var(--border)' : 'none',
                                                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                                }}>
                                                    {!isMe && (
                                                        <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--accent)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                            Kunde
                                                        </div>
                                                    )}
                                                    {m.text}
                                                    <div style={{ fontSize: '10px', opacity: 0.55, marginTop: '3px', textAlign: 'right' }}>
                                                        {fmtTime(m.created_at)}
                                                    </div>
                                                </div>
                                                {!isMe && delBtn}
                                            </div>
                                        );
                                    })}
                                    <div ref={bottomRef} />
                                </div>

                                <div style={{ padding: '8px 10px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', flexShrink: 0 }}>
                                    <textarea
                                        ref={inputRef}
                                        value={replyText}
                                        onChange={e => setReplyText(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                                        placeholder="Antwort schreiben…"
                                        style={{
                                            flex: 1, background: 'var(--bg-secondary)', border: '1.5px solid var(--border)',
                                            borderRadius: '8px', color: 'var(--text-primary)', fontFamily: 'inherit',
                                            fontSize: '13px', padding: '7px 10px', resize: 'none', outline: 'none',
                                            minHeight: '34px', maxHeight: '80px', lineHeight: 1.4,
                                        }}
                                        rows={1}
                                    />
                                    <button onClick={sendReply} disabled={!replyText.trim() || sending}
                                        style={{
                                            background: replyText.trim() && !sending ? 'var(--accent)' : 'var(--bg-secondary)',
                                            border: '1px solid var(--border)', borderRadius: '8px',
                                            cursor: replyText.trim() && !sending ? 'pointer' : 'default',
                                            padding: '7px 11px',
                                            color: replyText.trim() && !sending ? '#000' : 'var(--text-secondary)',
                                            transition: 'all 0.15s', flexShrink: 0, display: 'flex', alignItems: 'center',
                                        }}>
                                        <Send size={14} />
                                    </button>
                                </div>
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Toggle button */}
            <button
                onClick={() => { setOpen(o => !o); if (!open) { loadPreviews(); setView('list'); } }}
                title="Kunden-Chat"
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '44px', height: '44px', borderRadius: '50%', marginLeft: 'auto',
                    background: open ? 'var(--accent)' : 'var(--card-bg)',
                    border: `1.5px solid ${open ? 'var(--accent)' : 'var(--border)'}`,
                    cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
                    transition: 'all 0.15s', position: 'relative',
                }}>
                <MessageSquare size={19} color={open ? '#000' : 'var(--accent)'} />
                {!open && totalUnread > 0 && (
                    <span style={{
                        position: 'absolute', top: '-4px', right: '-4px',
                        background: '#ef4444', color: '#fff', fontSize: '11px', fontWeight: 700,
                        borderRadius: '20px', minWidth: '18px', height: '18px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '0 4px', border: '2px solid var(--card-bg)',
                    }}>
                        {totalUnread}
                    </span>
                )}
            </button>
        </div>
    );
}
